import { TIER_CODE } from "../sim/body-table";
import type { Vec3Like } from "./frame";
import type { ScaleMapper } from "./scale-mapper";

/** A point-tier orbit is shown while the camera is within this band of orbit radii from the parent. */
const NEAR_ORBIT_RADII = 0.5;
const FAR_ORBIT_RADII = 2;

/** A planet with fewer moons than this always shows all of them. */
export const SMALL_MOON_SYSTEM = 4;

/** Whether a point-tier body's orbit and label are shown this frame. */
export function pointOrbitShown(
  camToParent: number,
  orbitRadiusScene: number,
  isFocus: boolean,
  isHover: boolean,
): boolean {
  if (isFocus || isHover) return true;
  return (
    camToParent >= NEAR_ORBIT_RADII * orbitRadiusScene &&
    camToParent <= FAR_ORBIT_RADII * orbitRadiusScene
  );
}

export interface ShownInput {
  count: number;
  /** TIER_CODE per body. */
  tier: Uint8Array;
  parentIndex: Int32Array;
  /** Semi-major axis in km, 0 without an orbit. */
  orbitKm: Float64Array;
  positions: Float32Array;
  camera: Vec3Like;
  focus: number;
  hover: number;
}

/** out[k] = 1 for every non-point body, and for point bodies passing pointOrbitShown. */
export function fillShown(input: ShownInput, mapper: ScaleMapper, out: Uint8Array): void {
  const { count, tier, parentIndex, orbitKm, positions, camera, focus, hover } = input;
  for (let k = 0; k < count; k++) {
    const p = parentIndex[k];
    if (tier[k] !== TIER_CODE.point || p < 0) {
      out[k] = 1;
      continue;
    }
    const o = 3 * p;
    const d = Math.hypot(
      positions[o] - camera.x,
      positions[o + 1] - camera.y,
      positions[o + 2] - camera.z,
    );
    out[k] = pointOrbitShown(d, mapper.mapDistance(orbitKm[k]), k === focus, k === hover) ? 1 : 0;
  }
}

/** Gate entire moon systems using the current projected view, not the selected body. */
export function fillMoonVisibility(
  moonParents: Int32Array,
  moonCounts: Uint16Array,
  planets: readonly number[],
  screen: Float32Array,
  radiiPx: Float32Array,
  width: number,
  height: number,
  focus: number,
  out: Uint8Array,
): void {
  const inView = (k: number): boolean => {
    const o = 3 * k,
      r = radiiPx[k];
    return (
      screen[o + 2] > 0 &&
      screen[o] + r >= 0 &&
      screen[o] - r <= width &&
      screen[o + 1] + r >= 0 &&
      screen[o + 1] - r <= height
    );
  };
  let solePlanet = -1;
  for (const k of planets) {
    if (!inView(k)) continue;
    if (solePlanet >= 0) {
      solePlanet = -2;
      break;
    }
    solePlanet = k;
  }
  for (let k = 0; k < out.length; k++) {
    const parent = moonParents[k];
    // Keep a directly selected moon visible while following it, even if its parent is offscreen.
    out[k] =
      parent < 0 ||
      moonCounts[parent] < SMALL_MOON_SYSTEM ||
      k === focus ||
      parent === solePlanet ||
      (inView(parent) && radiiPx[parent] >= 24)
        ? 1
        : 0;
  }
}
